/* eslint-disable sonarjs/no-duplicate-string */
import throttle from 'raf-throttle'
import debounce from 'lodash.debounce'
import {
  UICorePlugin,
  Playback,
  template,
  type EventMap,
  type AttributeMap,
  Browser,
  type TemplateStringifier,
  type SupportedVersion,
  $
} from '@clappr/core'
import type {Options, RecommenderOptions} from 'common/types/options'
import {
  getSuggest,
  getRecordsByFilter,
  type RecordsByFilterParams
} from 'common/api/filters'
import {getRecommendations} from 'common/api/recommendations'
import {
  type MultiPlayerConfig,
  AdvertisementBlockType,
  Errors
} from 'common/api/records'
import {Events} from 'common/types/events'
import {injectStyle, loadStyle} from 'utils/dom'
import {timeStampToString} from 'utils/date'
import {convertNumberToHex, isDark} from 'utils/colors'
import type {Core} from 'common/components/core'
import type {MediaControl} from 'common/plugins/media-control'
import type {Docking} from 'common/plugins/docking'
import type {
  Advertisement,
  AdvertisementEvent
} from 'common/plugins/advertisement'
import {UNKNOWN_ID} from 'common/constants'
import * as skins from './skins'

interface PlayListItem {
  id: number
  uuid: string
  name: string
  preview: string
  duration: string
  durationMs: number
  link: string
  rcmId?: string
  showId?: string
}

export class MultiPlayer<T extends Options> extends UICorePlugin<T> {
  public static NEXT_RECORD_TIMEOUT = 3500
  public static IMPRESSION_TIMEOUT = 300

  private static template = ''
  private static classNames: Record<string, string> = {}

  public static async preload(options: Options): Promise<Options | void> {
    if (options.multiplayer && !MultiPlayer.template) {
      const skinName = options.mediaControl?.skinName
      const loadSkin = (skinName && (skins as any)[skinName]) || skins.rambler

      if (typeof loadSkin !== 'function') {
        return
      }

      const {default: template, classNames} = await loadSkin()

      if (template) {
        MultiPlayer.template = template
      }

      if (classNames) {
        MultiPlayer.classNames = classNames
      }
    }
  }

  public core!: Core<T>
  private rendered = false
  private playList?: PlayListItem[]
  private nextRecordTimeout?: number
  private nextRecordRequested = false
  private nextRecordAwaited = false
  private mainItemDuration = 0
  private prevItemsDurationMap?: {[id: number]: number}
  private activeItem?: number
  private $playList!: ZeptoCollection

  public get name(): string {
    return 'multiplayer'
  }

  public get prevItemsDuration(): null | number {
    const {activeItem, mainItemDuration, prevItemsDurationMap} = this

    if (activeItem && prevItemsDurationMap) {
      if (this.isMainRecord) {
        return 0
      }

      const duration = prevItemsDurationMap?.[activeItem]

      if (duration != null) {
        return mainItemDuration + duration
      }
    }

    return null
  }

  public get supportedVersion(): SupportedVersion {
    return {min: '0.5.0'}
  }

  private get classNames(): Record<string, string> {
    return MultiPlayer.classNames
  }

  public get template(): TemplateStringifier {
    return template(MultiPlayer.template)
  }

  public get events(): EventMap {
    return {
      'click [data-id]': 'playRecord',
      'click [data-multiplayer-prev]': 'scrollToStart',
      'click [data-multiplayer-next]': 'scrollToEnd'
    }
  }

  public get attributes(): AttributeMap {
    const className = this.classNames.root ?? 'eplayer-multiplayer'

    return {
      class: `${className} eplayer-dont-remove-reload`,
      'data-multiplayer': ''
    }
  }

  public get params(): MultiPlayerConfig {
    return this.core.options.multiplayer || ({} as MultiPlayerConfig)
  }

  private get recommender(): null | RecommenderOptions {
    return this.core.options.recommender || null
  }

  private get isHidden(): boolean {
    return Browser.isMobile || !!this.core.options.multiplayerHidden
  }

  private get canPlay(): boolean {
    return this.core.activePlayback.getPlaybackType() !== Playback.NO_OP
  }

  private get mediaControl(): MediaControl<T> | null {
    return this.core.getPlugin('mediaControl') as MediaControl<T> | null
  }

  private get advertisement(): Advertisement<T> | null {
    return this.core.getPlugin('advertisement') as Advertisement<T> | null
  }

  private get docking(): Docking<T> | null {
    return this.core.getPlugin('docking') as Docking<T> | null
  }

  // TODO(VP-4598): Избавиться от использования глобальных классов
  // Использование локальных классов скина имеет приоритет
  private get $activeRecord(): ZeptoCollection {
    const className = this.classNames.activeItem ?? 'multiplayer-active-item'

    return this.$el.find(`.${className}`)
  }

  public get isMainRecord(): boolean {
    return this.$activeRecord.length === 0
  }

  public get isLastRecord(): boolean {
    const id = this.core.options.playList?.id
    const last = this.playList?.[this.playList.length - 1]

    return id && last ? id === last.id : false
  }

  public get nextRecord(): PlayListItem | null {
    const id = this.core.options.playList?.id
    const {playList} = this

    if (id && playList?.length) {
      if (this.isMainRecord || this.isLastRecord) {
        return playList[0]
      }

      for (let index = 0; index < playList.length; index++) {
        if (playList[index].id === id) {
          return playList[index + 1]
        }
      }
    }

    return null
  }

  public isNextRecordRequested(): boolean {
    return this.nextRecordRequested
  }

  public isNextRecordAwaited(): boolean {
    return this.nextRecordAwaited
  }

  public bindEvents(): void {
    this.listenToOnce(this.core, Events.CORE_READY, this.onReady)
    this.listenTo(
      this.core,
      Events.CORE_ACTIVE_CONTAINER_CHANGED,
      this.onActiveContainerChange
    )

    this.listenTo(this.core, Events.CORE_RESIZE, this.toggleArrowsVisibility)

    if (this.mediaControl) {
      this.listenTo(
        this.mediaControl,
        Events.MEDIACONTROL_RENDERED,
        this.render
      )
    }

    const {advertisement} = this

    if (advertisement) {
      this.listenTo(
        advertisement,
        Events.ADVERTISEMENT_LOADING,
        this.onAdLoading
      )
      this.listenTo(
        advertisement,
        Events.ADVERTISEMENT_STARTED,
        this.onAdStarted
      )
      this.listenTo(advertisement, Events.ADVERTISEMENT_ENDED, this.onAdEnded)
    }

    if (this.docking) {
      this.listenTo(this.docking, Events.DOCKING_CHANGED, this.onDockingChange)
    }

    const {activeContainer} = this.core

    if (activeContainer) {
      this.listenTo(activeContainer, Events.CONTAINER_PLAY, this.stopNextRecord)
      this.listenTo(
        activeContainer,
        Events.CONTAINER_ENDED,
        this.onActiveContainerEnded
      )
    }
  }

  public stopListening(
    object?: unknown,
    name?: string,
    callback?: (event: any) => void
  ): this {
    if (!callback) {
      this.unsubscribeToScroll()
    }

    return super.stopListening(object, name, callback)
  }

  public onActiveContainerChange(): void {
    this.stopListening()
    this.bindEvents()
    this.activeItem = this.core.options.playList?.id ?? undefined
  }

  private subscribeToScroll = (): void => {
    this.unsubscribeToScroll()
    this.$playList?.on('scroll', this.toggleArrowsVisibility)
    this.$playList?.on('scroll', this.sendImpression)
  }

  private unsubscribeToScroll = (): void => {
    this.$playList?.off('scroll', this.toggleArrowsVisibility)
    this.$playList?.off('scroll', this.sendImpression)
  }

  private onActiveContainerEnded(): void {
    if (this.isLastRecord) {
      this.mainItemDuration = 0
    }

    if (this.advertisement?.hasAd()) {
      this.listenToOnce(
        this.advertisement,
        Events.ADVERTISEMENT_CONTENT_RESUME_REQUESTED,
        this.autoPlayNextRecord
      )
    } else {
      this.autoPlayNextRecord()
    }
  }

  private async onReady(): Promise<void> {
    if (!this.playList && this.canPlay) {
      let fetchPromise

      if (this.recommender) {
        fetchPromise = this.fetchRecommendations()
      } else if (this.params.filterId) {
        fetchPromise = this.fetchRecordsByFilter(this.params.filterId)
      } else if (this.params.url) {
        fetchPromise = this.fetchPlayList()
      }

      if (fetchPromise) {
        await Promise.all([this.injectStyles(), fetchPromise])
      }
    }

    if (this.playList?.length || this.canPlay) {
      this.render()
    }
  }

  private async injectStyles(): Promise<void> {
    const style = document.querySelector(`style[data-scope="${this.name}"]`)

    if (style) {
      return
    }

    const {fontUrl, textColor, color, backgroundColor} = this.params
    const styles: string[] = []

    if (textColor != null) {
      styles.push(`--multiplayer-text-color: ${convertNumberToHex(textColor)}`)
    }

    if (color != null) {
      const colorString = convertNumberToHex(color)
      const isColorDark = isDark(colorString)

      styles.push(`--multiplayer-color: ${colorString}`)

      this.$el
        .toggleClass('eplayer-multiplayer-dark-color', isColorDark)
        .toggleClass('eplayer-multiplayer-light-color', !isColorDark)
    }

    if (backgroundColor != null) {
      styles.push(
        `--multiplayer-bg-color: ${convertNumberToHex(backgroundColor)}`
      )
    }

    if (styles.length) {
      const rootElementClassName = this.classNames.root ?? 'eplayer-multiplayer'

      injectStyle(`.${rootElementClassName} {${styles.join(';')}}`, this.name)
    }

    if (fontUrl && fontUrl.indexOf('fonts.googleapis.com') > -1) {
      const styleSheet = await loadStyle(fontUrl)

      injectStyle(
        styleSheet.replace(
          /font-family: '.+';/g,
          'font-family: eagle-multiplayer-font;'
        ),
        this.name
      )
    }
  }

  private async fetchPlayList(): Promise<any> {
    const {
      url,
      filterId, // eslint-disable-line @typescript-eslint/no-unused-vars
      ...rest
    } = this.params

    if (this.playList || !url) {
      return
    }

    try {
      const records = await getSuggest(url, rest)

      this.playList = records
        .filter(
          (record) =>
            !!record.current_screenshot_small &&
            record.is_processed &&
            record.id !== this.core.options.playList?.id
        )
        .map((record) => ({
          id: record.id,
          uuid: record.uuid,
          name: record.name,
          preview: record.current_screenshot_small,
          duration: timeStampToString(Number(record.duration)),
          durationMs: Number(record.duration),
          link: record.link
        }))
      this.setDurations()
      this.trigger(Events.MULTIPLAYER_SUGGEST_LOADED, {url})
    } catch {
      this.trigger(Events.MULTIPLAYER_SUGGEST_ERROR)
    }
  }

  private async fetchRecordsByFilter(filterId: number): Promise<any> {
    if (this.playList) {
      return
    }

    const params: RecordsByFilterParams = {
      filterId,
      pageLimit: 10,
      orderDir: this.core.options.multiplayerShuffle ? 'random' : 'desc'
    }

    try {
      const records = await getRecordsByFilter(params)

      this.playList = records
        .filter(
          (record) =>
            !!record.currentScreenshotSmall &&
            record.isProcessed &&
            record.id !== this.core.options.playList?.id
        )
        .map((record) => ({
          id: record.id,
          uuid: record.uuid,
          name: record.name,
          preview: record.currentScreenshotSmall ?? '',
          duration: timeStampToString(Number(record.duration)),
          durationMs: Number(record.duration),
          link: record.link
        }))
      this.trigger(Events.MULTIPLAYER_SUGGEST_LOADED, {params})
    } catch (error: any) {
      if (error.code === Errors.CACHE_NOT_READY.code && this.params.url) {
        await this.fetchPlayList()
      } else {
        this.trigger(Events.MULTIPLAYER_SUGGEST_ERROR)
      }
    }
  }

  private async fetchRecommendations(): Promise<any> {
    const blockId = this.recommender?.blockId

    if (this.playList || !blockId) {
      return
    }

    const url = `https://rcm.rambler.ru/api/v2/blocks/${blockId}/recommendations`

    try {
      const records = await getRecommendations(url, {
        limit: 10,
        item_id: this.core.options.playList?.link
      })

      this.playList = records
        .filter(
          (record) =>
            !!record.item_image &&
            Number(record.rambler_video_id) !== this.core.options.playList?.id
        )
        .map((record) => ({
          id: Number(record.rambler_video_id),
          // NOTE: response doesn't have record uuid
          uuid: UNKNOWN_ID,
          name: record.item_title,
          preview: record.item_image,
          duration: timeStampToString(Number(record.item_duration)),
          durationMs: Number(record.item_duration),
          link: record.item_id,
          rcmId: record.rcm_id,
          showId: record.show_id
        }))
      this.setDurations()
      this.trigger(Events.MULTIPLAYER_SUGGEST_LOADED, {url})
    } catch {
      this.trigger(Events.MULTIPLAYER_SUGGEST_ERROR, {recommender: true})
    }
  }

  private setDurations(): void {
    const {playList} = this

    this.mainItemDuration = this.core.options.playList?.duration ?? 0
    this.prevItemsDurationMap = playList?.reduce((result, {id}, index) => {
      const prevItem = playList[index - 1]

      result[id] = prevItem ? result[prevItem.id] + prevItem.durationMs : 0

      return result
    }, {} as {[id: number]: number})
  }

  protected playRecord(event: MouseEvent): void {
    const {id} = (event.currentTarget as HTMLElement).dataset

    if (!id || this.core.loading) {
      return
    }

    const index = this.$playList.find(`[data-id="${id}"]`).index()
    const item = this.playList?.[index]

    this.stopNextRecord()
    this.trigger(Events.MULTIPLAYER_SUGGEST_CLICK, {
      id: parseInt(id, 10),
      uuid: item?.uuid,
      index,
      total: this.playList?.length ?? 0,
      link: item?.link,
      rcmId: item?.rcmId,
      showId: item?.showId
    })

    // TODO(VP-4598): Избавиться от использования глобальных классов
    // Использование локальных классов скина имеет приоритет
    const className = this.classNames.activeItem ?? 'multiplayer-active-item'

    this.$activeRecord.removeClass(className)
    $(event.currentTarget).addClass(className)

    this.core.reload({id: item?.uuid, autoPlay: true} as T)
  }

  public waitNextRecord(): void {
    this.nextRecordAwaited = true
  }

  public stopNextRecord(): void {
    this.nextRecordRequested = false
    this.nextRecordAwaited = false
    clearTimeout(this.nextRecordTimeout)
    delete this.nextRecordTimeout
  }

  public autoPlayNextRecord(): void {
    if (this.nextRecordAwaited) {
      this.nextRecordAwaited = false

      if (!this.nextRecordTimeout) {
        this.playNext()
      }

      return
    }

    this.stopNextRecord()
    this.nextRecordRequested = true
    this.nextRecordTimeout = window.setTimeout(
      this.playNext,
      MultiPlayer.NEXT_RECORD_TIMEOUT
    )
    this.trigger(Events.MULTIPLAYER_SUGGEST_TIMEOUT)
  }

  private onAdLoading(event: AdvertisementEvent): void {
    const postRoll =
      event.blockType === AdvertisementBlockType.POST_ROLL ||
      event.blockType === AdvertisementBlockType.POST_ROLL_MOBILE

    if (!postRoll) {
      return
    }

    const {advertisement} = this

    this.stopNextRecord()
    this.stopListening(
      advertisement,
      Events.ADVERTISEMENT_LOADING,
      this.onAdLoading
    )
    this.listenToOnce(
      advertisement,
      Events.ADVERTISEMENT_CONTENT_RESUME_REQUESTED,
      () => {
        this.listenTo(
          advertisement,
          Events.ADVERTISEMENT_LOADING,
          this.onAdLoading
        )
        this.autoPlayNextRecord()
      }
    )
  }

  private onAdStarted(): void {
    this.$playList
      ?.children()
      .css({pointerEvents: 'none', filter: 'brightness(0.6)'})
  }

  private onAdEnded(): void {
    this.$playList?.children().css({pointerEvents: null, filter: null})
  }

  private onDockingChange(docked: boolean): void {
    this.$el.toggle(!docked && !this.isHidden)
  }

  public playNext = (): void => {
    delete this.nextRecordTimeout

    if (this.nextRecordAwaited || this.core.loading) {
      return
    }

    this.nextRecordRequested = false
    this.nextRecordAwaited = false

    if (!this.playList?.length) {
      return
    }

    let $nextRecord = this.$activeRecord.next()

    if ($nextRecord.length === 0) {
      $nextRecord = this.$playList.children(':first-child')
    }

    const index = $nextRecord.index()
    const item = this.playList[index]

    // TODO(VP-4598): Избавиться от использования глобальных классов
    // Использование локальных классов скина имеет приоритет
    const className = this.classNames.activeItem ?? 'multiplayer-active-item'

    this.$activeRecord.removeClass(className)
    $nextRecord.addClass(className)

    this.core.reload({id: item.uuid, autoPlay: true} as T)
  }

  protected toggleArrowsVisibility = throttle((): void => {
    const {classNames, $playList} = this
    const scrollElement = $playList?.get(0)

    if (!scrollElement) {
      return
    }

    // TODO(VP-4598): Избавиться от использования глобальных классов
    // Использование локальных классов скина имеет приоритет
    this.$el.toggleClass(
      classNames.leftArrowVisible ?? 'multiplayer-left-arrow-visible',
      scrollElement.scrollLeft > 0
    )

    this.$el.toggleClass(
      classNames.rightArrowVisible ?? 'multiplayer-right-arrow-visible',
      scrollElement.scrollLeft + scrollElement.offsetWidth <
        scrollElement.scrollWidth
    )
  })

  protected scrollToStart(): void {
    this.scrollByStep(-1)
  }

  protected scrollToEnd(): void {
    this.scrollByStep(1)
  }

  private get itemWidth(): null | number {
    const $items = this.$playList?.children()
    const firstItemOffset = $items?.eq(0).offset()
    const secondItemOffset = $items?.eq(1).offset()

    return (
      firstItemOffset &&
      secondItemOffset &&
      secondItemOffset.left - firstItemOffset.left
    )
  }

  private scrollByStep(step: 1 | -1): void {
    const scrollElement = this.$playList?.get(0)
    const {itemWidth} = this

    if (!scrollElement || !itemWidth) {
      return
    }

    scrollElement.scrollLeft += step * itemWidth
  }

  private scrollToActiveItem() {
    const scrollElement = this.$playList?.get(0)
    const {itemWidth} = this

    if (!scrollElement || !itemWidth) {
      return
    }

    if (this.$activeRecord?.length) {
      const {left} = this.$activeRecord.offset()

      scrollElement.scrollLeft += left - itemWidth
    } else {
      scrollElement.scrollLeft = 0
    }
  }

  private sendImpression = debounce((): void => {
    this.$playList.children().forEach((element: HTMLElement, index) => {
      const {id, shown} = element.dataset

      if (id && !shown) {
        const containerWidth = this.$el.width()
        const {right, left} = element.getBoundingClientRect()
        const isVisible = left >= 0 && right <= containerWidth

        if (isVisible) {
          const item = this.playList?.[index]

          element.dataset.shown = 'true'
          this.trigger(Events.MULTIPLAYER_SUGGEST_IMPRESSION, {
            id: parseInt(id, 10),
            uuid: item?.uuid,
            index,
            total: this.playList?.length ?? 0,
            link: item?.link,
            rcmId: item?.rcmId,
            showId: item?.showId
          })
        }
      }
    })
  }, MultiPlayer.IMPRESSION_TIMEOUT)

  public render(): this {
    if (!this.playList?.length) {
      return this
    }

    if (!this.rendered) {
      this.$el.html(this.template({playList: this.playList}))
      this.$playList = this.$el.find('[data-multiplayer-items]')
      // render outside the core one time to save plugin node in DOM
      this.core.$el.after(this.el)
      this.rendered = true
    }

    this.subscribeToScroll()

    if (this.isHidden) {
      this.$el.hide()
    } else {
      this.scrollToActiveItem()
      this.toggleArrowsVisibility()
      this.sendImpression()
    }

    this.trigger(Events.MULTIPLAYER_RENDERED)

    return this
  }

  // NOTE: replace original destroy to save plugin node in DOM
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public destroy(): void {}
}
